import { type BlockConfig, iconField, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `b2bProducts` block.
 *
 * Decomposed from `src/components/b2b/B2BProducts.tsx`. 2-column product
 * grid with icon, tier badge, use-case chips, and code-style endpoint.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `b2b-products.tsx` / `b2b-products.payload.ts`
 *   React component export:  `B2BProducts`
 *   Payload `slug`:          `b2bProducts`
 *   Payload `interfaceName`: `B2BProductsBlock`
 */

export const B2BProductsBlock: BlockConfig = {
  slug: 'b2bProducts',
  interfaceName: 'B2BProductsBlock',
  labels: {
    singular: 'B2B Products',
    plural: 'B2B Products Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow',
      defaultValue: 'Products',
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 2 },
    },
    {
      name: 'products',
      type: 'array',
      required: true,
      minRows: 1,
      maxRows: 8,
      label: 'Products',
      labels: { singular: 'Product', plural: 'Products' },
      fields: [
        {
          ...iconField({
            name: 'icon',
            label: 'Icon (lucide-react name)',
            required: true,
          }),
          admin: {
            description:
              'Allowed: Brain, Cloud, Code, Database, MessageSquare, Search, Sparkles, Target, TrendingUp, Users, Workflow.',
          },
        },
        { name: 'title', type: 'text', required: true, label: 'Product title' },
        { name: 'subtitle', type: 'text', label: 'Subtitle' },
        {
          name: 'description',
          type: 'textarea',
          required: true,
          label: 'Description',
          admin: { rows: 3 },
        },
        {
          name: 'tier',
          type: 'text',
          label: 'Tier badge',
          admin: {
            description: 'e.g. "Standard+", "Premium+", "All Tiers".',
          },
        },
        {
          name: 'endpoint',
          type: 'text',
          label: 'Endpoint',
          admin: {
            description:
              'Optional code-styled endpoint string (e.g. "POST /data-feed/query").',
          },
        },
        {
          name: 'useCases',
          type: 'array',
          label: 'Use cases',
          labels: { singular: 'Use case', plural: 'Use cases' },
          maxRows: 8,
          fields: [
            { name: 'text', type: 'text', required: true, label: 'Use case' },
          ],
        },
      ],
    },
    {
      name: 'cta',
      type: 'group',
      label: 'CTA (footer)',
      fields: [
        { name: 'text', type: 'text', required: true, label: 'Button text' },
        linkField({ name: 'link', label: 'Button link', required: true }),
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
      ],
    },
  ],
};

export default B2BProductsBlock;
